// ConsentContextProvider.tsx

import React, { ReactNode, useState, useEffect } from "react";
import { ConsentSettings, ConsentContext } from "../../Context/ConsentContext";
import ConfigService from "../../Core/ConfigService";
import ReactGA from 'react-ga4';

const COOKIE_CONSENT_KEY = 'sudoku_vs_cookie_consent';

// Default settings applied immediately on page load (denied)
const defaultConsent: ConsentSettings = {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
};

export const ConsentContextProvider = ({ children }: { children: ReactNode }) => {
    const [consent, setConsent] = useState<ConsentSettings | null>(null);
    const [showBanner, setShowBanner] = useState<boolean>(false);
    const [analyticsDetails, setAnalyticsDetails] = useState<boolean>(false);
    const [advertisingDetails, setAdvertisingDetails] = useState<boolean>(false);
    // Add state for partial consent
    const [partialConsent, setPartialConsent] = useState<ConsentSettings>({ ...defaultConsent });

    // Ensure dataLayer and gtag exist, and set default consent immediately
    const ensureGtagAndDefault = () => {
        window.dataLayer = window.dataLayer || [];
        if (typeof window.gtag !== 'function') {
            window.gtag = (...args: any[]) => window.dataLayer.push(args);
        }
        // Set default consent before any tag reads
        window.gtag('consent', 'default', {
            ad_storage: defaultConsent.ad_storage,
            analytics_storage: defaultConsent.analytics_storage,
            ad_user_data: defaultConsent.ad_user_data,
            ad_personalization: defaultConsent.ad_personalization,
        });
    };

    // Push updated consent when user confirms or stored settings
    const pushUpdatedConsent = (settings: ConsentSettings) => {
        window.gtag('consent', 'update', {
            ad_storage: settings.ad_storage,
            analytics_storage: settings.analytics_storage,
            ad_user_data: settings.ad_user_data,
            ad_personalization: settings.ad_personalization,
        });
    };

    // Initialize GA
    const initializeAnalytics = async () => {
        const config = await ConfigService.get();
        ReactGA.initialize(config.Analytics.TrackingId, {
            gaOptions: { cookieFlags: 'max-age=7200;secure;samesite=none' }
        });
    };

    // Toggle individual consent settings
    const toggleConsentSetting = (setting: keyof ConsentSettings) => {
        setPartialConsent(prev => ({
            ...prev,
            [setting]: prev[setting] === 'granted' ? 'denied' : 'granted'
        }));
    };

    // Confirm consent with current partial settings
    const confirmConsent = async () => {
        const newSettings: ConsentSettings = {
            ...partialConsent,
            timestamp: new Date().toISOString(),
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newSettings));
        pushUpdatedConsent(newSettings);

        // Only initialize analytics if analytics storage is granted
        if (newSettings.analytics_storage === 'granted') {
            await initializeAnalytics();
        }

        setConsent(newSettings);
        setShowBanner(false);
    };

    // Toggle details UI
    const toggleConsentDetail = (type: 'analytics' | 'advertising') => {
        if (type === 'analytics') setAnalyticsDetails(prev => !prev);
        else setAdvertisingDetails(prev => !prev);
    };

    // On mount: set default consent immediately, then apply stored and init
    useEffect(() => {
        // 1. Ensure gtag and set default before any tag reads
        ensureGtagAndDefault();

        const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (stored) {
            try {
                const parsed: ConsentSettings = JSON.parse(stored);
                // update to stored state
                pushUpdatedConsent(parsed);
                setPartialConsent(parsed);

                if (parsed.analytics_storage === 'granted') {
                    initializeAnalytics();
                }

                setConsent(parsed);
            } catch {
                // fallback: leave default, show banner
                setPartialConsent(defaultConsent);
                initializeAnalytics();
                setConsent(defaultConsent);
                setShowBanner(true);
            }
        } else {
            // No stored: keep default (denied) and show banner
            setPartialConsent(defaultConsent);
            initializeAnalytics();
            setConsent(defaultConsent);
            setShowBanner(true);
        }
    }, []);

    return (
        <ConsentContext.Provider
            value={{
                consent,
                confirmConsent,
                hasConsented: consent?.analytics_storage === 'granted',
                showBanner,
                toggleConsentDetail,
                analyticsDetails,
                advertisingDetails,
                partialConsent,
                toggleConsentSetting
            }}
        >
            {children}
        </ConsentContext.Provider>
    );
};